// Lightweight first-party analytics tracker (pageviews + named events).
// Sends to our own backend /api/track endpoint. Visitor identified by an
// anonymous id persisted in localStorage. Attribution (referrer + UTM) is
// captured once per session and replayed with every event.
//
// Privacy-safe: we never capture guest identity, emails, names, or any PII.
import api from "@/lib/api";

const VID_KEY = "hg_vid";
const ATTR_KEY = "hg_attr"; // sessionStorage: { referrer, utm_source, utm_medium, utm_campaign }

export function getVisitorId() {
  try {
    let vid = localStorage.getItem(VID_KEY);
    if (!vid) {
      vid =
        (window.crypto && typeof window.crypto.randomUUID === "function"
          ? window.crypto.randomUUID()
          : "v-" + Math.random().toString(36).slice(2) + Date.now().toString(36));
      localStorage.setItem(VID_KEY, vid);
    }
    return vid;
  } catch (e) {
    return "anon-" + Date.now().toString(36);
  }
}

// Capture attribution (referrer + UTM params) once per browser session.
// Subsequent calls reuse the stored values so attribution survives navigation.
function getAttribution() {
  try {
    const existing = sessionStorage.getItem(ATTR_KEY);
    if (existing) return JSON.parse(existing);

    const params = new URLSearchParams(window.location.search || "");
    const referrer = document.referrer || "";
    // Treat same-origin referrers as no external referrer.
    let externalReferrer = referrer;
    try {
      if (referrer && new URL(referrer).origin === window.location.origin) {
        externalReferrer = "";
      }
    } catch (e) {
      /* keep raw referrer */
    }

    const attr = {
      referrer: externalReferrer || null,
      utm_source: params.get("utm_source") || null,
      utm_medium: params.get("utm_medium") || null,
      utm_campaign: params.get("utm_campaign") || null,
    };
    sessionStorage.setItem(ATTR_KEY, JSON.stringify(attr));
    return attr;
  } catch (e) {
    return { referrer: null, utm_source: null, utm_medium: null, utm_campaign: null };
  }
}

function send(payload) {
  // Fire-and-forget; never let analytics break the app.
  try {
    const attr = getAttribution();
    // Send the real browser hostname so the backend can distinguish production
    // traffic from preview/localhost/internal traffic (proxies may rewrite Origin).
    const page_host = (typeof window !== "undefined" && window.location && window.location.hostname) || null;
    api.post("/track", { ...attr, page_host, ...payload }).catch(() => {});
  } catch (e) {
    /* no-op */
  }
}

export function trackPageview(path) {
  send({ kind: "pageview", visitor_id: getVisitorId(), path: path || window.location.pathname });
}

export function trackEvent(name, path) {
  send({ kind: "event", name, visitor_id: getVisitorId(), path: path || window.location.pathname });
}
